import { DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import { TargetBox } from 'components/DragDrop'
import { useRef, useState } from 'react'
import { NativeTypes } from 'react-dnd-html5-backend'
import { toast } from 'react-toastify'
import { type FeeSheetAiExtractResult, extractHud1FeesFromPdf, uploadFiles } from 'services'
import { Button, Modal } from 'stories/components'
import { getPrice2decimal } from 'utils'

type Props = {
  isOpen: boolean
  loanNumber: number
  onClose: () => void
  onApply: (result: FeeSheetAiExtractResult) => void
  onAddUnmatched?: (unmatched: FeeSheetAiExtractResult['unmatched']) => void
}

const isAcceptedFeeSheet = (name: string) => {
  const lower = (name || '').toLowerCase()
  return lower.endsWith('.pdf') || /\.(png|jpe?g|webp|gif|tiff?)$/i.test(lower)
}

export const FeeSheetAiExtractModal = ({ isOpen, loanNumber, onClose, onApply, onAddUnmatched }: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [loading, setLoading] = useState(false)
  const [fileName, setFileName] = useState('')
  const [result, setResult] = useState<FeeSheetAiExtractResult | null>(null)

  const reset = () => {
    setLoading(false)
    setFileName('')
    setResult(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const runExtractFromFileKey = async (fileKey: string, name: string) => {
    setLoading(true)
    setResult(null)
    setFileName(name)
    try {
      const res = await extractHud1FeesFromPdf([{ fileKey: String(fileKey), name }])
      if (res?.success && res.data) {
        setResult(res.data)
        if (!res.data.fees.length && !res.data.unmatched.length) {
          toast('No fees were found in the document.', { type: 'warning' })
        }
      } else {
        toast(res?.message || 'Failed to extract fees from the document.', { type: 'error' })
      }
    } catch {
      toast('Failed to extract fees from the document.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const runExtract = async (file: File) => {
    if (!isAcceptedFeeSheet(file.name)) {
      toast('Please use a PDF or image fee sheet.', { type: 'error' })
      return
    }
    setLoading(true)
    setResult(null)
    setFileName(file.name)
    try {
      const keys: string[] = await uploadFiles(
        {
          path: `loan/${loanNumber}/fee-sheet-ai`,
        },
        [file],
      )
      const fileKey = keys?.[0]
      if (!fileKey) {
        toast('Failed to upload fee sheet document.', { type: 'error' })
        return
      }

      const res = await extractHud1FeesFromPdf([{ fileKey: String(fileKey), name: file.name }])
      if (res?.success && res.data) {
        setResult(res.data)
        if (!res.data.fees.length && !res.data.unmatched.length) {
          toast('No fees were found in the document.', { type: 'warning' })
        }
      } else {
        toast(res?.message || 'Failed to extract fees from the document.', { type: 'error' })
      }
    } catch {
      toast('Failed to extract fees from the document.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onFilePicked = (files: FileList | File[] | null) => {
    const file = files?.[0]
    if (!file) return
    runExtract(file)
  }

  const onDropItem = (item: any) => {
    // OS / browser file drop via react-dnd NativeTypes.FILE
    if (item?.files?.length) {
      onFilePicked(item.files)
      return
    }

    // Document floater drag (type: 'document') — already on S3
    const docs: any[] = Array.isArray(item?.documents) ? item.documents : [item]
    const doc = docs.find((d) => d?.fileKey || d?.key) || docs[0]
    const fileKey = doc?.fileKey || doc?.key
    const name = doc?.name || 'document.pdf'
    if (!fileKey) {
      toast('Could not read the dropped document.', { type: 'error' })
      return
    }
    if (!isAcceptedFeeSheet(name)) {
      toast('Please drop a PDF or image fee sheet.', { type: 'error' })
      return
    }
    runExtractFromFileKey(String(fileKey), name)
  }

  return (
    <Modal
      title="Extract Fees from Fee Sheet"
      isOpen={isOpen}
      onClose={handleClose}
      titleOkay={result?.fees?.length ? 'Apply Matched Fees' : ''}
      onOk={() => {
        if (!result?.fees?.length) return
        onApply(result)
        handleClose()
      }}
      loading={loading}
      disabled={!result?.fees?.length}
    >
      <div className="w-[720px] max-w-full space-y-4">
        <p className="text-sm text-gray-600">
          Drop a fee sheet PDF (from your computer or the Documents floater). AI will match fees to existing Fee
          Worksheet lines. Review the matches, then apply — remember to save the worksheet afterward.
        </p>

        <TargetBox
          types={['document', NativeTypes.FILE]}
          onDrop={onDropItem}
          className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors border-gray-300 hover:border-shade-blue"
          highlightClassName="border-shade-blue bg-blue-50"
        >
          <div onClick={() => inputRef.current?.click()}>
            <DocumentArrowUpIcon className="w-10 h-10 mx-auto text-shade-blue mb-2" />
            <div className="font-medium text-gray-800">Drop fee sheet PDF here</div>
            <div className="text-sm text-gray-500 mt-1">or click to browse</div>
            {fileName && <div className="text-xs text-gray-500 mt-2">Selected: {fileName}</div>}
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,application/pdf,image/*"
              className="hidden"
              onChange={(e) => onFilePicked(e.target.files)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </TargetBox>

        {loading && <div className="text-sm text-shade-blue font-medium">AI is reading the fee sheet…</div>}

        {result && (
          <div className="space-y-4">
            <div>
              <div className="font-semibold mb-2">Matched fees ({result.fees.length})</div>
              {result.fees.length === 0 ? (
                <div className="text-sm text-gray-500">No fees matched existing worksheet lines.</div>
              ) : (
                <div className="overflow-auto border rounded max-h-64">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left">
                      <tr>
                        <th className="p-2">Line</th>
                        <th className="p-2">Name</th>
                        <th className="p-2 text-right">Borrower</th>
                        <th className="p-2 text-right">Seller</th>
                        <th className="p-2">Paid To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.fees.map((fee, idx) => (
                        <tr key={`${fee.hudLineNo}-${idx}`} className="border-t">
                          <td className="p-2">{fee.hudLineNo2010 || fee.hudLineNo}</td>
                          <td className="p-2">{fee.name || '—'}</td>
                          <td className="p-2 text-right">
                            {fee.borrowerAmount != null ? getPrice2decimal(fee.borrowerAmount, false, true) : '—'}
                          </td>
                          <td className="p-2 text-right">
                            {fee.sellerAmount != null ? getPrice2decimal(fee.sellerAmount, false, true) : '—'}
                          </td>
                          <td className="p-2">{fee.paidToName || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {!!result.unmatched.length && (
              <div>
                <div className="font-semibold mb-2 text-amber-700">Unmatched ({result.unmatched.length})</div>
                <div className="overflow-auto border rounded max-h-40">
                  <table className="w-full text-sm">
                    <thead className="bg-amber-50 text-left">
                      <tr>
                        <th className="p-2">Name</th>
                        <th className="p-2 text-right">Amount</th>
                        <th className="p-2">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.unmatched.map((item, idx) => (
                        <tr key={`${item.name}-${idx}`} className="border-t">
                          <td className="p-2">{item.name || '—'}</td>
                          <td className="p-2 text-right">
                            {item.amount != null ? getPrice2decimal(item.amount, false, true) : '—'}
                          </td>
                          <td className="p-2 text-gray-500">{item.notes || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Unmatched fees are not on the worksheet yet. Use “Add unmatched fees” to add them from the fee catalog
                  (same as “Add new fee”), then save.
                </p>
                {onAddUnmatched && (
                  <div className="mt-2">
                    <Button
                      link
                      color="blue"
                      onClick={() => {
                        // Keep matched apply separate; stash unmatched for the add-fee flow.
                        if (result.fees.length) onApply(result)
                        onAddUnmatched(result.unmatched)
                        handleClose()
                      }}
                    >
                      Add unmatched fees
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end">
              <Button
                link
                color="gray"
                onClick={() => {
                  reset()
                }}
              >
                Choose another file
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
